import React, { useState } from "react";
import { Divider, Stack, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import TriggerForm, { TTrigger } from "./triggers/TriggerForm";
import ConditionForm from "./conditions/ConditionForm";
import ActionForm, { TAction } from "./actions/ActionForm";
import useUpdateData from "../../hooks/useUpdateData";
import useCreateData from "../../hooks/useCreateData";
import { IDevice } from "../devices/types";
import { IControl } from "../controls/types";
import { IAutomation } from "./types";
import { TMqttTrigger } from "./triggers/types";
import DialogButtons from "../../Components/DialogButtons";
import { TCondition } from "./conditions/types";
import { useTranslation } from "react-i18next";

interface IAutomationFormProps {
  automation: IAutomation;
  devices: IDevice[];
  controls: IControl[];
}

const AutomationForm = (props: IAutomationFormProps) => {
  const { automation, devices, controls } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: automation.name
        ? t("Editing automation AUTOMATION cancelled", { automation })
        : t("Creating new automation cancelled"),
      severity: "info",
    });
    navigate("/automations");
  };

  const createHandler = useCreateData();
  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      name: automation.name,
      is_enabled: automation.is_enabled,
      trigger: automation.trigger,
      condition: automation.condition,
      action: automation.action,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const allValues = { ...automation, ...values };
      if (automation.id === -1) {
        createHandler(
          `automation "${allValues.name}"`,
          "/automations",
          allValues
        );
      } else {
        updateHandler(
          `automation "${allValues.name}"`,
          `/automations/${automation.id}`,
          allValues
        );
      }
      navigate("/automations");
    },
  });
  return (
    <form onSubmit={formik.handleSubmit} data-cy="automation-form">
      <FormikProvider value={formik}>
        <Stack
          spacing={2}
          alignItems="flex-start"
          divider={<Divider orientation="horizontal" flexItem />}
        >
          <TextField
            fullWidth
            autoFocus
            margin="normal"
            id="name"
            label="Name"
            type="text"
            disabled={isSubmitting}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
          ></TextField>
          <TriggerForm
            trigger={formik.values.trigger}
            devices={devices}
            onChange={(trigger: TTrigger) =>
              formik.setFieldValue("trigger", trigger)
            }
          />
          <ConditionForm
            condition={formik.values.condition}
            devices={devices}
            device_id={
              (formik.values.trigger as TMqttTrigger)?.device_id || undefined
            }
            attribute={
              (formik.values.trigger as TMqttTrigger)?.topic || undefined
            }
            onChange={(condition: TCondition) =>
              formik.setFieldValue("condition", condition)
            }
          />
          <ActionForm
            action={formik.values.action}
            controls={controls}
            onChange={(action: TAction) =>
              formik.setFieldValue("action", action)
            }
          />
        </Stack>
        <DialogButtons
          isSubmitting={isSubmitting}
          submitButtonLabel="Save"
          resetCallback={handleCancel}
        />
      </FormikProvider>
    </form>
  );
};

export default AutomationForm;
